import { useEffect, useState, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { dailyAPI, inboxAPI } from '@/lib/api';
import { getRecentNotes } from '@/lib/recentNotes';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from '@/components/ui/command';
import {
  Pencil, Search, Plus, Star, Archive, Trash, FolderOpen, Tag,
  Zap, Calendar, Inbox, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function CommandPalette({ onOpenQuickCapture }) {
  const [open, setOpen] = useState(false);
  const {
    notes, setSelectedNoteId, createNote, fetchNotes, fetchNotebooks,
    setActiveView, setFilterNotebookId, setFilterTagId,
    notebooks, tags,
  } = useApp();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'n' && !e.shiftKey) {
        e.preventDefault();
        createNote();
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'N') {
        e.preventDefault();
        onOpenQuickCapture?.();
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        openDailyNote();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [createNote, onOpenQuickCapture]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectNote = (noteId) => {
    setSelectedNoteId(noteId);
    setOpen(false);
  };

  const handleNewNote = () => {
    createNote();
    setOpen(false);
  };

  const handleNav = (view, nbId = null, tagId = null) => {
    setActiveView(view);
    setFilterNotebookId(nbId);
    setFilterTagId(tagId);
    setOpen(false);
  };

  const openDailyNote = useCallback(async () => {
    try {
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const res = await dailyAPI.getOrCreate(dateStr);
      if (res.data.created) await fetchNotes();
      setSelectedNoteId(res.data.note.id);
      setOpen(false);
      if (res.data.created) toast.success(`Created daily note: ${dateStr}`);
    } catch {
      toast.error('Failed to open daily note');
    }
  }, [fetchNotes, setSelectedNoteId]);

  const handleQuickCapture = () => {
    setOpen(false);
    onOpenQuickCapture?.();
  };

  const activeNotes = notes.filter((n) => !n.is_deleted && !n.is_archived).slice(0, 20);
  const recentViewed = getRecentNotes()
    .filter((r) => notes.some((n) => n.id === r.id && !n.is_deleted))
    .slice(0, 5);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search notes, tag:name, notebook:name, is:pinned..." data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Quick Actions">
          <CommandItem onSelect={handleNewNote} data-testid="command-new-note">
            <Plus className="w-4 h-4 mr-2" />
            New Note
            <span className="ml-auto text-xs text-muted-foreground">Ctrl+N</span>
          </CommandItem>
          <CommandItem onSelect={handleQuickCapture} data-testid="command-quick-capture">
            <Zap className="w-4 h-4 mr-2" />
            Quick Capture
            <span className="ml-auto text-xs text-muted-foreground">Ctrl+Shift+N</span>
          </CommandItem>
          <CommandItem onSelect={openDailyNote} data-testid="command-daily-note">
            <Calendar className="w-4 h-4 mr-2" />
            Today's Note
            <span className="ml-auto text-xs text-muted-foreground">Ctrl+Shift+T</span>
          </CommandItem>
        </CommandGroup>

        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => handleNav('all')} data-testid="command-all-notes">
            <Pencil className="w-4 h-4 mr-2" />
            All Notes
          </CommandItem>
          <CommandItem onSelect={() => handleNav('favorites')} data-testid="command-favorites">
            <Star className="w-4 h-4 mr-2" />
            Favorites
          </CommandItem>
          <CommandItem onSelect={() => handleNav('archived')} data-testid="command-archive">
            <Archive className="w-4 h-4 mr-2" />
            Archive
          </CommandItem>
          <CommandItem onSelect={() => handleNav('trash')} data-testid="command-trash">
            <Trash className="w-4 h-4 mr-2" />
            Trash
          </CommandItem>
        </CommandGroup>

        {recentViewed.length > 0 && (
          <CommandGroup heading="Recently Viewed">
            {recentViewed.map((r) => (
              <CommandItem key={r.id} onSelect={() => handleSelectNote(r.id)} data-testid={`command-recent-${r.id}`}>
                <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="truncate">{r.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {notebooks.length > 0 && (
          <CommandGroup heading="Notebooks">
            {notebooks.map((nb) => (
              <CommandItem key={nb.id} onSelect={() => handleNav('all', nb.id)} data-testid={`command-notebook-${nb.id}`}>
                <FolderOpen className="w-4 h-4 mr-2" />
                {nb.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {tags.length > 0 && (
          <CommandGroup heading="Tags">
            {tags.map((tag) => (
              <CommandItem key={tag.id} onSelect={() => handleNav('all', null, tag.id)} data-testid={`command-tag-${tag.id}`}>
                <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: tag.color }} />
                {tag.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {activeNotes.length > 0 && (
          <CommandGroup heading="Notes">
            {activeNotes.map((note) => (
              <CommandItem key={note.id} onSelect={() => handleSelectNote(note.id)} data-testid={`command-note-${note.id}`}>
                <Search className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="truncate">{note.title || 'Untitled'}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
